import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ASSET_KEYS, type AccountOptionsDto, type AssetDto, type AssetInput, type AssetKindDto, type AssetKindInput, type AssetOptionsDto, type AssetsDto, type DeportResult, type DepreciationRowDto, type DepreciationsDto, type PermissionKey } from "@delta/shared";
import { useLang, type T } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Blocked, Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "../settings/common";
import { AccName, AccountSelect, accText, useAccountOptions } from "../accounting/common";
import { amt } from "../ledger/common";

// Fixed assets › Asset kinds, All assets and Depreciation, ported from the prototype (erp-assets.js "/assets/types",
// "/assets/all", "/assets/depreciation"). Rules T-1, T-2 and T-4 to T-7 are provisional (docs/proposed-financial-rules.md,
// section 11). The server works out every schedule and posts every deport; these screens only show and ask.

export const assetCrumbs = (t: T, label: string, href?: string, extra: [string, string?][] = []): [string, string?][] => [[t("home"), "/home"], [t("fixedAssets")], [label, href], ...extra];
export const AssetProvisional = () => { const { t } = useLang(); return <div className="dep-note" data-testid="asset-provisional"><Icon name="info" /><span>{t("assetProvisional")}</span></div>; };

/** Kinds, warehouses, treasuries, suppliers and tax types for the asset screens. */
export function useAssetOptions(): AssetOptionsDto | null {
  const [o, setO] = useState<AssetOptionsDto | null>(null);
  useEffect(() => { api<AssetOptionsDto>("GET", "/assets/options").then(setO, () => setO(null)); }, []);
  return o;
}

const newButton = (can: (k: PermissionKey) => boolean, t: T, k: PermissionKey, label: string, run: () => void) =>
  can(k) ? <button className="btn btn-primary" onClick={run}><Icon name="plus" />{label}</button> : <LockedButton label={label} title={t("noPermissionFor", { p: k })} />;

/** The chart's accounts of one level-1 group (1 Assets, 4 Expenses), for the kind's account fields (T-1). */
const ofGroup = (o: AccountOptionsDto | null, level1: number): AccountOptionsDto | null => {
  if (!o) return null;
  const types = o.types.filter((x) => x.level1Id === level1);
  const ids = new Set(types.map((x) => x.id));
  return { ...o, types, accounts: o.accounts.filter((a) => ids.has(a.typeId)) };
};

// ---------- asset kinds (T-1, T-6) ----------

const KIND = ASSET_KEYS.kinds;
const KIND_ACC: ["assetAccountId" | "accumulatedAccountId" | "expenseAccountId", string, number][] = [["assetAccountId", "accountIndex", 1], ["accumulatedAccountId", "depAccount", 1], ["expenseAccountId", "depExpAccount", 4]];

function KindForm({ k, onClose, onSaved }: { k: AssetKindDto | null; onClose(): void; onSaved(x: AssetKindDto, created: boolean): void }) {
  const { t } = useLang();
  const { opts, failed } = useAccountOptions();
  const [v, setV] = useState<AssetKindInput>(() => k
    ? { nameAr: k.nameAr, nameEn: k.nameEn, months: k.months, assetAccountId: k.assetAccountId, accumulatedAccountId: k.accumulatedAccountId, expenseAccountId: k.expenseAccountId }
    : { nameAr: "", nameEn: "", months: 60, assetAccountId: "", accumulatedAccountId: "", expenseAccountId: "" });
  const [months, setMonths] = useState(String(v.months));
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof AssetKindInput>(key: K, x: AssetKindInput[K]) => { setV((o) => ({ ...o, [key]: x })); setErrs((e) => ({ ...e, [key]: "" })); };
  async function save() {
    const e: Record<string, string> = {};
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    if (!/^\d{1,4}$/.test(months.trim()) || +months < 1 || +months > 1200) e.months = t("monthsWhole");
    for (const [f] of KIND_ACC) if (!v[f]) e[f] = t("requiredField");
    if (!e.accumulatedAccountId && v.assetAccountId && v.assetAccountId === v.accumulatedAccountId) e.accumulatedAccountId = t("depSameAccount");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    try {
      const body = { ...v, months: Number(months) };
      onSaved(k ? await api<AssetKindDto>("PUT", `/asset-kinds/${k.id}`, body) : await api<AssetKindDto>("POST", "/asset-kinds", body), !k);
    } catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); } finally { setBusy(false); }
  }
  const lock = !!k?.locked;
  return (
    <Modal title={t(k ? "editAssetKind" : "newAssetKind")} wide onClose={onClose} onSubmit={() => void save()} alert={alert} busy={busy}>
      {lock && <div className="field full"><div className="dep-note" data-testid="kind-locked"><Icon name="lock" /><span>{t("kindLockedNote")}</span></div></div>}
      <Field label={t("nameAr")} req error={errs.nameAr} htmlFor="ak-ar"><input id="ak-ar" className="control" lang="ar" dir="rtl" value={v.nameAr} onChange={(e) => set("nameAr", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("nameEn")} req error={errs.nameEn} htmlFor="ak-en"><input id="ak-en" className="control" lang="en" dir="ltr" value={v.nameEn} onChange={(e) => set("nameEn", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("depPeriod")} req error={errs.months} htmlFor="ak-months">
        <input id="ak-months" className="control num" style={{ textAlign: "start" }} dir="ltr" inputMode="numeric" value={months} disabled={lock} onChange={(e) => { setMonths(e.target.value); setErrs((x) => ({ ...x, months: "" })); }} autoComplete="off" />
      </Field>
      {KIND_ACC.map(([f, label, g]) => (
        <Field key={f} label={t(label)} req error={errs[f]} htmlFor={`ak-${f}`}>
          <AccountSelect id={`ak-${f}`} value={v[f]} onChange={(x) => set(f, x)} opts={ofGroup(opts, g)} failed={failed || lock} />
        </Field>
      ))}
    </Modal>
  );
}

export function AssetKinds() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const [rows, setRows] = useState<AssetKindDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<AssetKindDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<AssetKindDto | null>(null);
  const [blocked, setBlocked] = useState<AssetKindDto | null>(null);
  const load = useCallback(async () => {
    setFailed(null);
    try { setRows(await api<AssetKindDto[]>("GET", `/asset-kinds${filters.name ? "?q=" + encodeURIComponent(filters.name) : ""}`)); } catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, t]);
  useEffect(() => { void load(); }, [load]);
  async function remove(k: AssetKindDto) {
    try { await api("DELETE", `/asset-kinds/${k.id}`); toast(t("deletedOk", { x: L(k) })); } catch (e) { toast(errorText(t, e)); }
    await load();
  }
  const columns: Column<AssetKindDto>[] = [
    { k: "name", label: t("kindName"), cell: (k) => <><span className="cell-name">{L(k)}</span>{k.locked && <> <span className="pill" data-testid="kind-locked-pill"><Icon name="lock" /></span></>}</>, csv: (k) => L(k) },
    { k: "months", label: t("depPeriod"), end: true, cell: (k) => <span className="num">{k.months}</span>, csv: (k) => k.months },
    { k: "acc", label: t("accountsIndex"), cell: (k) => <AccName a={k.assetAccount} />, csv: (k) => accText(L, k.assetAccount) },
    { k: "dep", label: t("depAccount"), cell: (k) => <AccName a={k.accumulatedAccount} />, csv: (k) => accText(L, k.accumulatedAccount) },
    { k: "exp", label: t("depExpAccount"), cell: (k) => <AccName a={k.expenseAccount} />, csv: (k) => accText(L, k.expenseAccount) },
    { k: "n", label: t("assetsCount"), end: true, cell: (k) => <span className="num">{k.assetsCount}</span>, csv: (k) => k.assetsCount }
  ];
  return (
    <Shell navKey="/assets/types">
      <PageHead crumbs={assetCrumbs(t, t("assetTypes"))} title={t("assetTypes")} />
      <ListView rows={rows} error={failed} columns={columns} file="asset-kinds" testId="asset-kinds-table" onRefresh={() => void load()}
        search={[{ k: "name", label: t("kindName"), type: "text" }]} filters={filters} onFilters={setFilters}
        quick={(k) => `${k.nameEn} ${k.nameAr}`} newButton={newButton(s.can, t, KIND.add, t("newAssetKind"), () => setEditing("new"))}
        empty={{ icon: "building", title: t("noResults"), body: t("noResultsBody") }}
        actions={(k) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(k), ...(s.can(KIND.update) ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => (k.assetsCount ? setBlocked(k) : setDeleting(k)), ...(s.can(KIND.del) ? {} : lockTag(t)) }
        ]} />
      {editing && <KindForm k={editing === "new" ? null : editing} onClose={() => setEditing(null)}
        onSaved={(k, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(k) })); void load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => { const k = deleting; setDeleting(null); void remove(k); }} />}
      {blocked && <Blocked title={t("cantDelete")} body={`${t("cantDeleteAssetKind")} (${t("refCount_assets", { n: blocked.assetsCount })})`} onClose={() => setBlocked(null)} />}
    </Shell>
  );
}

// ---------- all assets (T-2, T-6, T-7) ----------

const ASSET = ASSET_KEYS.assets;
const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;

function AssetForm({ a, kinds, onClose, onSaved }: { a: AssetDto | null; kinds: AssetOptionsDto["kinds"]; onClose(): void; onSaved(x: AssetDto, created: boolean): void }) {
  const { t, L } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const [v, setV] = useState<AssetInput>(() => a
    ? { title: a.title, kindId: a.kind.id, amount: a.amount, startOn: a.startOn, depreciates: a.depreciates }
    : { title: "", kindId: kinds[0]?.id ?? "", amount: "", startOn: today, depreciates: true });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof AssetInput>(k: K, x: AssetInput[K]) => { setV((o) => ({ ...o, [k]: x })); setErrs((e) => ({ ...e, [k]: "" })); };
  async function save() {
    const e: Record<string, string> = {};
    const amount = String(v.amount).trim();
    if (!v.title.trim()) e.title = t("requiredField");
    if (!v.kindId) e.kindId = t("requiredField");
    if (!v.startOn) e.startOn = t("requiredField");
    if (!amount) e.amount = t("requiredField");
    else if (!MONEY_RE.test(amount)) e.amount = /\.\d{3,}$/.test(amount) ? t("twoDecimals") : t("positiveNumber");
    else if (!/[1-9]/.test(amount)) e.amount = t("positiveNumber");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    try {
      const body = { ...v, title: v.title.trim(), amount };
      onSaved(a ? await api<AssetDto>("PUT", `/assets/${a.id}`, body) : await api<AssetDto>("POST", "/assets", body), !a);
    } catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); } finally { setBusy(false); }
  }
  const kind = kinds.find((k) => k.id === v.kindId);
  return (
    <Modal title={t(a ? "editFixedAsset" : "newFixedAsset")} onClose={onClose} onSubmit={() => void save()} alert={alert} busy={busy}>
      {!a && <div className="field full"><div className="dep-note" data-testid="asset-posts-nothing"><Icon name="info" /><span>{t("assetPostsNothing")}</span></div></div>}
      <Field label={t("assetName")} req full error={errs.title} htmlFor="as-title"><input id="as-title" className="control" value={v.title} maxLength={200} onChange={(e) => set("title", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("fixedAssetAmount")} req error={errs.amount} htmlFor="as-amount">
        <input id="as-amount" className="control num" style={{ textAlign: "start" }} dir="ltr" inputMode="decimal" value={String(v.amount)} onChange={(e) => set("amount", e.target.value)} autoComplete="off" />
      </Field>
      <Field label={t("fixedAssetKind")} req error={errs.kindId} htmlFor="as-kind" hint={kind ? `${t("depPeriod")}: ${kind.months}` : undefined}>
        <select id="as-kind" className="control" value={v.kindId} onChange={(e) => set("kindId", e.target.value)}><option value="">{t("choose")}</option>{kinds.map((k) => <option key={k.id} value={k.id}>{L(k)}</option>)}</select>
      </Field>
      <Field label={t("startWorkDate")} req error={errs.startOn} htmlFor="as-start"><input id="as-start" type="date" className="control num" style={{ textAlign: "start" }} value={v.startOn} onChange={(e) => set("startOn", e.target.value)} /></Field>
      <div className="field full"><label className="check-row"><input type="checkbox" checked={v.depreciates} onChange={(e) => set("depreciates", e.target.checked)} data-testid="as-depreciates" /> {t("calcMonthly")}</label></div>
    </Modal>
  );
}

export function AllAssets() {
  const { t, L } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const o = useAssetOptions();
  const [data, setData] = useState<AssetsDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<AssetDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<AssetDto | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const load = useCallback(async () => {
    setFailed(null);
    const q = [filters.name && `q=${encodeURIComponent(filters.name)}`, filters.kind && `kindId=${filters.kind}`].filter(Boolean).join("&");
    try { setData(await api<AssetsDto>("GET", `/assets${q ? "?" + q : ""}`)); } catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, filters.kind, t]);
  useEffect(() => { void load(); }, [load]);
  async function remove(a: AssetDto) {
    try { await api("DELETE", `/assets/${a.id}`); toast(t("deletedOk", { x: a.title })); } catch (e) { toast(errorText(t, e)); }
    await load();
  }
  const lockText = (a: AssetDto) => t(a.invoice ? "assetFromPurchaseLock" : "assetDeportedLock");
  const columns: Column<AssetDto>[] = [
    { k: "name", label: t("assetName"), cell: (a) => <><span className="cell-name">{a.title}</span>{a.cancelled && <> <span className="pill" data-testid="asset-cancelled">{t("assetCancelled")}</span></>}
      {a.invoice && <div className="hint"><Link to={`/assets-purchase/show/${a.invoice.id}`}>{t("assetFromInvoice")} <span className="num">{a.invoice.code}</span></Link></div>}</>, csv: (a) => a.title },
    { k: "amount", label: t("fixedAssetAmount"), end: true, cell: (a) => <span className="num">{amt(a.amount)}</span>, csv: (a) => a.amount },
    { k: "monthly", label: t("depreciationMonthly"), end: true, cell: (a) => (a.depreciates && !a.cancelled ? <span className="num">{amt(a.monthly)}</span> : <span className="muted">{t("noDepreciation")}</span>), csv: (a) => a.monthly },
    { k: "kind", label: t("fixedAssetKind"), cell: (a) => L(a.kind), csv: (a) => L(a.kind) },
    { k: "start", label: t("startWorkDate"), cell: (a) => <span className="num">{a.startOn}</span>, csv: (a) => a.startOn },
    { k: "acc", label: t("accumulatedDep"), end: true, cell: (a) => <span className="num">{amt(a.accumulated)}</span>, csv: (a) => a.accumulated },
    { k: "book", label: t("bookValue"), end: true, cell: (a) => <b className="num">{amt(a.bookValue)}</b>, csv: (a) => a.bookValue }
  ];
  const kinds = o?.kinds ?? [];
  const above = data && <div className="ltb-above"><span className="hint" data-testid="assets-total">{t("fixedAssetAmount")}: <b className="num">{amt(data.total.amount)}</b> · {t("accumulatedDep")}: <b className="num">{amt(data.total.accumulated)}</b> · {t("bookValue")}: <b className="num">{amt(data.total.bookValue)}</b></span></div>;
  return (
    <Shell navKey="/assets/all">
      <PageHead crumbs={assetCrumbs(t, t("allAssets"))} title={t("allAssets")} />
      <AssetProvisional />
      <ListView rows={data?.assets ?? null} error={failed} columns={columns} file="assets" testId="assets-table" onRefresh={() => void load()} above={above}
        search={[{ k: "name", label: t("assetName"), type: "text" }, { k: "kind", label: t("fixedAssetKind"), type: "select", options: kinds.map((k) => [k.id, L(k)]) }]} filters={filters} onFilters={setFilters}
        quick={(a) => `${a.title} ${L(a.kind)} ${a.invoice?.code ?? ""}`} newButton={newButton(s.can, t, ASSET.add, t("newFixedAsset"), () => setEditing("new"))}
        empty={{ icon: "building", title: t("noAssets"), body: t("noAssetsBody") }}
        actions={(a) => [
          { label: t("scheduleRows"), icon: "calendar", run: () => nav(`/assets/depreciation?asset=${a.id}`) },
          { label: t("edit"), icon: "edit", run: () => (a.locked ? setBlocked(lockText(a)) : setEditing(a)), ...(s.can(ASSET.update) ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => (a.locked ? setBlocked(lockText(a)) : setDeleting(a)), ...(s.can(ASSET.del) ? {} : lockTag(t)) }
        ]} />
      {editing && <AssetForm a={editing === "new" ? null : editing} kinds={kinds} onClose={() => setEditing(null)}
        onSaved={(a, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: a.title })); void load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => { const a = deleting; setDeleting(null); void remove(a); }} />}
      {blocked && <Blocked title={t("cantEdit")} body={blocked} onClose={() => setBlocked(null)} />}
    </Shell>
  );
}

// ---------- depreciation (T-4, T-5) ----------

const DEP = ASSET_KEYS.depreciation;
type Tab = "all" | "open" | "deported";

export function Depreciation() {
  const { t, L } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const [qs] = useSearchParams();
  const only = qs.get("asset");
  const [tab, setTab] = useState<Tab>("all");
  const [data, setData] = useState<DepreciationsDto | null>(null);
  const [asset, setAsset] = useState<AssetDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [ask, setAsk] = useState<{ kind: "due" } | { kind: "one" | "undo"; r: DepreciationRowDto } | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const load = useCallback(async () => {
    setFailed(null);
    const q = [only && `assetId=${only}`, tab !== "all" && `status=${tab}`].filter(Boolean).join("&");
    try {
      setData(await api<DepreciationsDto>("GET", `/asset-depreciations${q ? "?" + q : ""}`));
      setAsset(only ? await api<AssetDto>("GET", `/assets/${only}`).catch(() => null) : null);
    } catch (e) { setFailed(errorText(t, e)); }
  }, [only, tab, t]);
  useEffect(() => { void load(); }, [load]);
  async function run(url: string, ok: (x: DeportResult | DepreciationRowDto) => string) {
    try { toast(ok(await api<DeportResult | DepreciationRowDto>("POST", url, url.endsWith("deport-due") ? (only ? { assetId: only } : {}) : undefined))); } catch (e) { toast(errorText(t, e)); }
    await load();
  }
  const name = (r: DepreciationRowDto) => `${r.asset.title} ${r.fromOn.slice(0, 7)}`;
  function confirmed() {
    const a = ask; setAsk(null);
    if (!a) return;
    if (a.kind === "due") void run("/asset-depreciations/deport-due", (x) => t("deportedToast", { x: String((x as DeportResult).deported) }));
    else if (a.kind === "one") void run(`/asset-depreciations/${a.r.id}/deport`, () => t("deportedToast", { x: name(a.r) }));
    else void run(`/asset-depreciations/${a.r.id}/undeport`, () => t("undeportedToast", { x: name(a.r) }));
  }
  const columns: Column<DepreciationRowDto>[] = [
    { k: "asset", label: t("assetTitle"), cell: (r) => <><span className="cell-name">{r.asset.title}</span>{r.asset.cancelled && <> <span className="pill">{t("assetCancelled")}</span></>}</>, csv: (r) => r.asset.title },
    { k: "dep", label: t("depAccount"), cell: (r) => <AccName a={r.accumulatedAccount} />, csv: (r) => accText(L, r.accumulatedAccount) },
    { k: "exp", label: t("depExpAccount"), cell: (r) => <AccName a={r.expenseAccount} />, csv: (r) => accText(L, r.expenseAccount) },
    { k: "from", label: t("fromF"), cell: (r) => <span className="num">{r.fromOn}</span>, csv: (r) => r.fromOn },
    { k: "to", label: t("toF"), cell: (r) => <span className="num">{r.toOn}</span>, csv: (r) => r.toOn },
    { k: "amount", label: t("amount"), end: true, cell: (r) => <span className="num">{amt(r.amount)}</span>, csv: (r) => r.amount },
    { k: "status", label: t("isDeported"), cell: (r) => (r.deported ? <span className="pill good" data-testid="dep-status">{t("deportedYes")}</span>
      : <span className={`pill ${data && r.toOn <= data.today ? "warn" : ""}`} data-testid="dep-status">{t("deportedNo")}</span>), csv: (r) => t(r.deported ? "yes" : "no") },
    { k: "entry", label: t("voucherJournal"), cell: (r) => (r.entry ? <Link className="num" to={`/journals/show/${r.entry.id}`}>{r.entry.number}</Link> : <span className="muted">–</span>), csv: (r) => r.entry?.number ?? "" }
  ];
  const canDeport = s.can(DEP.deport), canUndo = s.can(DEP.undeport);
  const tools = canDeport ? <button className="btn btn-sm btn-primary" data-testid="dep-due" onClick={() => (data?.dueCount ? setAsk({ kind: "due" }) : setBlocked(t("nothingDue")))}><Icon name="ledger" /><span className="hide-sm">{t("deportUpTo")}</span></button>
    : <LockedButton label={t("deportUpTo")} title={t("noPermissionFor", { p: DEP.deport })} />;
  const above = <div className="ltb-above">
    {asset && <span className="fchip">{t("assetTitle")}: <b>{asset.title}</b><Link to="/assets/depreciation" aria-label={t("removeFilter")}><Icon name="x" /></Link></span>}
    {asset && <span className="hint" data-testid="dep-asset-values">{t("accumulatedDep")}: <b className="num">{amt(asset.accumulated)}</b> · {t("bookValue")}: <b className="num">{amt(asset.bookValue)}</b> · {t("depLife", { n: asset.periods, a: amt(asset.monthly) })}</span>}
    {data && <span className="hint" data-testid="dep-today">{t("depToday", { d: data.today })} {t("scheduleTotal")}: <b className="num">{amt(data.total)}</b> · {t("deportedTotal")}: <b className="num">{amt(data.deported)}</b></span>}
  </div>;
  const tabs: [Tab, string][] = [["all", t("all")], ["open", t("deportedNo")], ["deported", t("deportedYes")]];
  return (
    <Shell navKey="/assets/depreciation">
      <PageHead crumbs={assetCrumbs(t, t("assetDepreciation"))} title={t("assetDepreciation")} />
      <AssetProvisional />
      <div className="tabs" role="tablist" style={{ marginBottom: 12 }}>
        {tabs.map(([k, label]) => <button key={k} role="tab" aria-selected={tab === k} className={`tab ${tab === k ? "on" : ""}`} onClick={() => setTab(k)} data-testid={`dep-tab-${k}`}>{label}</button>)}
      </div>
      <ListView rows={data?.rows ?? null} error={failed} columns={columns} file="depreciation" testId="dep-table" onRefresh={() => void load()} tools={tools} above={above}
        quick={(r) => `${r.asset.title} ${r.fromOn} ${r.toOn}`} empty={{ icon: "calendar", title: t("noPeriods"), body: t("noPeriodsBody") }}
        actions={(r) => [
          r.deported
            ? { label: t("undeport"), icon: "refund", danger: true, run: () => (r.undeportable ? setAsk({ kind: "undo", r }) : setBlocked(t("notDueErr"))), ...(canUndo ? {} : lockTag(t)) }
            : { label: t("deport"), icon: "ledger", disabled: !r.due || !canDeport, ...(canDeport ? {} : { tag: t("noPermission") }), run: () => setAsk({ kind: "one", r }) },
          ...(r.entry ? [{ label: t("voucherJournal"), icon: "ledger" as const, run: () => nav(`/journals/show/${r.entry!.id}`) }] : []),
          { label: t("allAssets"), icon: "building", run: () => nav("/assets/all") }
        ]} />
      {ask?.kind === "due" && data && <Confirm title={t("deportUpTo")} body={t("deportDueBody", { n: data.dueCount, d: data.today, amt: amt(data.dueAmount) })} okLabel={t("deport")} onClose={() => setAsk(null)} onOk={confirmed} />}
      {ask?.kind === "one" && <Confirm title={t("deportTitle")} body={t("deportBody", { amt: `${amt(ask.r.amount)} ${t("currency")}` })} okLabel={t("deport")} onClose={() => setAsk(null)} onOk={confirmed} />}
      {ask?.kind === "undo" && <Confirm title={t("undeport")} body={t("undeportWarn")} okLabel={t("undeport")} onClose={() => setAsk(null)} onOk={confirmed} />}
      {blocked && <Blocked title={t("deportUpTo")} body={blocked} onClose={() => setBlocked(null)} />}
    </Shell>
  );
}
